import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Checkbox } from '@mui/material';
import { SaveParams, saveLocalParams, useModal, Images, PermissionList, RolePermission } from 'utils';
import { DateTypeSelector, SelectTypeAutocomplete, AddButton, CloseButton, ManageActions, CheckboxesTags } from 'components';
import { claimPaymentActions, fundingSourceActions, systemActions } from 'store';
import { AddClaimPayment } from './addClaimPayment';
import { remittanceClearinghouseList, remittanceSourceEnums, remittanceSourceList } from '../constants';

const SYNC_ERAS_ACTION_TYPE = 'SYNC_TL_CLEARINGHOUSE_ERAS';
// Syncing only creates the payments these users could add by hand, so payment posters may run it too.
const SYNC_ERAS_PERMISSIONS = [PermissionList.MANAGE_INTEGRATIONS?.code, PermissionList.CLAIM_PMT_PAY?.code];

export const CompletedClaimFilters = ({ afterSuccess }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const pushInfo = location?.state;
    const fundingSourceList = useSelector((state) => state.fundingSource.fundingSourceList);
    const canUseIntegrations = useSelector((state) => state.system.canUseIntegrations);
    const { open } = useModal();
    const canSyncEras =
        canUseIntegrations?.activeTlClearinghouse &&
        canUseIntegrations?.automationPaymentEnabled &&
        RolePermission(SYNC_ERAS_PERMISSIONS);

    const activeFiltersCount =
        (pushInfo?.payer ? 1 : 0) +
        (pushInfo?.source ? 1 : 0) +
        (pushInfo?.clearinghouse ? 1 : 0) +
        (pushInfo?.createdFrom || pushInfo?.createdTo ? 1 : 0) +
        (pushInfo?.paymentDateFrom || pushInfo?.paymentDateTo ? 1 : 0) +
        (pushInfo?.verified ? 1 : 0) +
        (pushInfo?.needsReview ? 1 : 0);

    useEffect(() => {
        dispatch(fundingSourceActions.getFundingSource({ sortType: 'name', orderType: 'ASC' }));
        if (RolePermission(SYNC_ERAS_PERMISSIONS)) {
            dispatch(systemActions.canUseIntegration());
        }
    }, []);

    const handleChange = (e) => {
        const info = {
            ...pushInfo,
        };
        info.skip = 0;
        info.limit = 50;
        info.page = 1;
        // Display-name companions (`funderName`, `clientFull`) ride on the nav state next to
        // the id they describe — the id is what the API filters on, the companion is what the
        // dropdown and the active-filter chip render. Both have to be cleared with their id or
        // the name outlives the filter.
        if (e.target.value === 'All' || !e.target.value) {
            delete info[e.target.name];
            if (e.target.name === 'payer') delete info.funderName;
            if (e.target.name === 'client') delete info.clientFull;
        } else {
            info[e.target.name] = e.target.value;
            if (e.target.name === 'payer') {
                info.funderName = fundingSourceList?.funders?.find((f) => f?.id === e.target.value)?.name;
            }
            if (e.target.name === 'client') {
                info['clientFull'] = e.target?.full;
            }
        }

        const localParams = {};
        info?.payer ? (localParams['payer'] = info?.payer) : delete localParams['payer'];
        // Mirrored from state like every other key — scoping this to `e.target.name === 'payer'`
        // dropped the name from the snapshot whenever any OTHER filter changed, so a restore
        // brought the payer back without its name.
        info?.funderName ? (localParams.funderName = info?.funderName) : delete localParams['funderName'];
        info?.createdFrom ? (localParams.createdFrom = info?.createdFrom) : delete localParams['createdFrom'];
        info?.createdTo ? (localParams.createdTo = info?.createdTo) : delete localParams['createdTo'];
        info?.paymentDateFrom ? (localParams.paymentDateFrom = info?.paymentDateFrom) : delete localParams['paymentDateFrom'];
        info?.paymentDateTo ? (localParams.paymentDateTo = info?.paymentDateTo) : delete localParams['paymentDateTo'];

        saveLocalParams('claimFilters', localParams);
        SaveParams(navigate, { ...info });
    };

    const handleResetAllFilters = (handleClose) => {
        const params = {
            ...pushInfo,
        };
        delete params.payer;
        delete params.funderName;
        delete params.clientFull;
        delete params.source;
        delete params.clearinghouse;
        delete params.createdFrom;
        delete params.createdTo;
        delete params.paymentDateFrom;
        delete params.paymentDateTo;
        delete params.verified;
        delete params.needsReview;
        params.skip = 0;
        params.limit = 50;
        params.page = 1;
        saveLocalParams('claimFilters', {});
        SaveParams(navigate, { ...params });

        if (handleClose) {
            handleClose();
        }
    };

    // Persist payer + both date ranges to sessionStorage so they survive a remount.
    // `value` is the fully-merged params object the DateTypeSelector hands back.
    const persistDateParams = (value) => {
        const localParams = {};
        value?.payer ? (localParams.payer = value?.payer) : delete localParams['payer'];
        value?.funderName ? (localParams.funderName = value?.funderName) : delete localParams['funderName'];
        value?.createdFrom ? (localParams.createdFrom = value?.createdFrom) : delete localParams['createdFrom'];
        value?.createdTo ? (localParams.createdTo = value?.createdTo) : delete localParams['createdTo'];
        value?.paymentDateFrom ? (localParams.paymentDateFrom = value?.paymentDateFrom) : delete localParams['paymentDateFrom'];
        value?.paymentDateTo ? (localParams.paymentDateTo = value?.paymentDateTo) : delete localParams['paymentDateTo'];
        saveLocalParams('claimFilters', localParams);
    };

    return (
        <div style={{ marginBottom: 12 }}>
            <div className="table-invoice-filter-section">
                <div className="billing-invoices-filter-box" style={{ alignItems: 'flex-end' }}>
                    <ManageActions
                        styleBodyItems={{ padding: 0 }}
                        btnClass={`filter-passive-btn ${activeFiltersCount > 0 ? 'active-filter' : ''}`}
                        renderBtnView={(anchorEl) => (
                            <div>
                                {activeFiltersCount > 0 && <div className="active-filters-count-badge">{activeFiltersCount}</div>}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <img src={Images.filters} alt="icon" />
                                </div>
                            </div>
                        )}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        wrapperStyles={{ position: 'relative' }}
                        renderBody={(handleClose) => (
                            <div style={{ minWidth: 300 }}>
                                <div className="schedule-filter-popper-header">
                                    <div className="flex-align-center" style={{ gap: 8 }}>
                                        <img src={Images.filters} alt="icon" />
                                        <p>Filters</p>
                                    </div>
                                    <CloseButton handleCLic={handleClose} />
                                </div>

                                <div className="schedule-filter-popper-body">
                                    <div className="filter-popper-body-selectors">
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.payer ? '' : 'All'}
                                            name={'payer'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.payer}
                                            defaultSelected={{ name: pushInfo?.funderName }}
                                            list={
                                                fundingSourceList?.funders
                                                    ? [{ id: 'All', name: 'All' }, ...fundingSourceList?.funders]
                                                    : [{ id: 'All', name: 'All' }]
                                            }
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Payer'}
                                            noError={true}
                                        />
                                        {/* Source — Automation (ERA) vs Manual */}
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.source ? '' : 'All'}
                                            name={'source'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.source}
                                            defaultSelected={{
                                                name: remittanceSourceList?.find((i) => i?.id === pushInfo?.source)?.name,
                                            }}
                                            list={[
                                                { id: 'All', name: 'All' },
                                                { id: remittanceSourceEnums.AUTOMATION, name: 'Automation' },
                                                { id: remittanceSourceEnums.MANUAL, name: 'Manual' },
                                            ]}
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Source'}
                                            noError={true}
                                        />
                                        {/* Clearinghouse — which one delivered an automated payment's ERA */}
                                        <SelectTypeAutocomplete
                                            placeholder={pushInfo?.clearinghouse ? '' : 'All'}
                                            name={'clearinghouse'}
                                            handleSelect={handleChange}
                                            defaultValue={pushInfo?.clearinghouse}
                                            defaultSelected={{
                                                name: remittanceClearinghouseList?.find((i) => i?.id === pushInfo?.clearinghouse)?.name,
                                            }}
                                            list={remittanceClearinghouseList}
                                            renderValue={(i) => i?.name}
                                            uiType={'tableFilter'}
                                            outLabel={'Clearinghouse'}
                                            noError={true}
                                        />
                                        {/* Created At range */}
                                        <DateTypeSelector
                                            outLabel={'Created At'}
                                            startName={'createdFrom'}
                                            endName={'createdTo'}
                                            storageSave={persistDateParams}
                                        />
                                        {/* Payment Date range */}
                                        <DateTypeSelector
                                            outLabel={'Payment Date'}
                                            startName={'paymentDateFrom'}
                                            endName={'paymentDateTo'}
                                            storageSave={persistDateParams}
                                        />
                                    </div>

                                    <div className="schedule-filter-checkboxes-wrapper">
                                        <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                            <Checkbox
                                                onClick={() =>
                                                    handleChange({
                                                        target: {
                                                            value: !pushInfo?.verified,
                                                            name: 'verified',
                                                        },
                                                    })
                                                }
                                                style={{
                                                    padding: 0,
                                                    color: pushInfo?.verified ? '#347AF0' : '#D0D5DD',
                                                    borderWidth: 1,
                                                }}
                                                checked={pushInfo?.verified}
                                            />
                                            <p style={{ margin: 0 }}>verified only</p>
                                        </div>
                                        <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                            <Checkbox
                                                onClick={() =>
                                                    handleChange({
                                                        target: {
                                                            value: !pushInfo?.needsReview,
                                                            name: 'needsReview',
                                                        },
                                                    })
                                                }
                                                style={{
                                                    padding: 0,
                                                    color: pushInfo?.needsReview ? '#347AF0' : '#D0D5DD',
                                                    borderWidth: 1,
                                                }}
                                                checked={pushInfo?.needsReview}
                                            />
                                            <p style={{ margin: 0 }}>needs review</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="schedule-filter-popper-footer">
                                    <button
                                        disabled={activeFiltersCount === 0}
                                        style={{
                                            opacity: activeFiltersCount > 0 ? 1 : 0.5,
                                            pointerEvents: activeFiltersCount > 0 ? 'auto' : 'none',
                                        }}
                                        onClick={() => handleResetAllFilters(handleClose)}>
                                        <img src={Images.revertWhite} alt="reset" />
                                        <p>Reset all</p>
                                    </button>

                                    <p className="schedule-filter-footer-result">
                                        {activeFiltersCount} result{activeFiltersCount !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                        )}
                    />
                </div>

                <div className="flex-align-center" style={{ gap: 16 }}>
                    {canSyncEras && (
                        <AddButton
                            text={'Sync'}
                            type={SYNC_ERAS_ACTION_TYPE}
                            customIcon={Images.refreshWhite}
                            handleClick={() => dispatch(claimPaymentActions.syncTlClearinghouseEras())}
                        />
                    )}
                    <AddButton
                        text={'Add Payment'}
                        handleClick={() =>
                            open(<AddClaimPayment afterSuccess={afterSuccess} />, { styles: { headerStyle: { display: 'none' } } })
                        }
                    />
                </div>
            </div>
        </div>
    );
};
